import { Form } from '@/components/ui/forms/form';
import { ptBrCommon } from '@/lib/pt-br/messages';
import type { SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { CepInput, ISearchProduct, Product } from '@/types';
import { useCallback, useState } from 'react';
import InputButton from '@/components/ui/forms/input-button';
import { applyMaskPostalCode } from '@/lib/utils/mask';
import { isPostalCodeValid } from '@/lib/utils/validation';
import { twMerge } from 'tailwind-merge';
import cn from 'classnames';
import { isPsychotropic, needRecipe } from '@/lib/utils/recipe';
import { formatPrice } from '@/lib/use-price';
import { useMutation } from 'react-query';
import { CloseIcon } from '@/components/icons/close-icon';
import { LocationIcon } from '@/components/icons/location';
import ProductLoader from '@/components/ui/loaders/product-loader';

const cepFormValidation = yup.object().shape({
  cep: yup
    .string()
    .required('error-email-required')
    .test('cep', 'error-invalid-postal-code', (value) => {
      return isPostalCodeValid(value);
    }),
});

function CepForm({
  cep,
  onSubmit,
  isLoading,
  serverError,
}: Readonly<{
  cep?: string;
  onSubmit: SubmitHandler<CepInput>;
  isLoading?: boolean;
  serverError?: any;
}>) {
const handleChange = (event: any) => {
    const maskedValue = applyMaskPostalCode(event.target.value);
    event.target.value = maskedValue;
  };

  return (
    <Form
      onSubmit={onSubmit}
      useFormProps={{
        defaultValues: { cep },
      }}
      validationSchema={cepFormValidation}
      serverError={serverError && ptBrCommon(serverError)}
      className="text-left w-full mt-[15px]"
    >
      {({ register, formState: { errors, isDirty, isValid } }) => (
        <div className="flex items-center gap-4">
          <InputButton
            className="w-full"
            type="text"
            placeholder="Digite seu cep"
            variant="complementary"
            {...register('cep', {
              onChange: handleChange,
            })}
            error={ptBrCommon(errors.cep?.message!)}
            disabledButton={!isDirty || !isValid}
          />
        </div>
      )}
    </Form>
  );
}

interface TableDataProps {
  firstElement?: boolean;
  children?: React.ReactNode;
  error?: boolean;
}

function TableData({ firstElement, children }: Readonly<TableDataProps>) {
  return (
    <td
      className={cn('w-full flex items-center pr-4', {
        'gap-2.5': firstElement,
      })}
    >
      {children}
    </td>
  );
}

interface TableRowProps {
  children?: React.ReactNode;
  error?: boolean;
  alert?: boolean;
}

function TableRow({ children, error, alert }: Readonly<TableRowProps>) {
  return (
    <tr
      className={twMerge(
        cn(
          'flex items-center py-[10px] pl-[40px] pr-[15px] even:bg-[rgba(25,127,219,0.03)]',
          {
            'bg-[rgba(240,86,122,0.03)]': error,
            'bg-[rgba(244,130,114,0.03)]': alert,
          },
        ),
      )}
    >
      {children}
    </tr>
  );
}

const renderShippingMethod = (
  { delivery_time, final_shipping_cost },
  convertedDeliveryTiming,
  product,
) => {
  return (
    <TableRow>
      <TableData>
        <div className="flex items-center w-full">
          <p>
            {/* {formattedDeliverySettings(
                { deliveryDays, shippingMethodId, label },
                convertedDeliveryTiming,
                product,
              )} */}
            {delivery_time}
          </p>

          {final_shipping_cost !== 0 && (
            <p className="ml-[4px]">
              -{' '}
              <span className="text-accent">
                {formatPrice({ amount: final_shipping_cost })}
              </span>
            </p>
          )}
          {final_shipping_cost === 0 && (
            <p className="ml-[4px]">
              - <span className="text-green-600">Grátis</span>
            </p>
          )}
        </div>
      </TableData>
    </TableRow>
  );
};

interface ShippingTableProps {
  shippingMethods: any[];
  convertedDeliveryTiming: any;
  product: Product;
  acceptedTypesRecipe: string[];
  address: string;
  handleCloseList: () => void;
}

function ShippingTable({
  shippingMethods,
  convertedDeliveryTiming,
  product,
  acceptedTypesRecipe,
  address,
  handleCloseList,
}: Readonly<ShippingTableProps>) {
  return (
    <table className="mt-4 border border-accent rounded-lg w-full table-fixed border-separate">
      <tbody>
        <TableRow>
          <TableData firstElement>
            <div className="flex items-center w-full">
              <LocationIcon className="-ml-8 absolute text-gray-200 w-[20px] h-[20px]" />
              <p className="overflow-hidden text-ellipsis whitespace-nowrap w-[80%]">
                {address}
              </p>
              <div className="flex-1 flex justify-end cursor-pointer">
                <CloseIcon onClick={handleCloseList} />
              </div>
            </div>
          </TableData>
        </TableRow>

        {shippingMethods.map((obj) =>
          renderShippingMethod(obj, convertedDeliveryTiming, product),
        )}

        {isPsychotropic(product) &&
          !!acceptedTypesRecipe.length &&
          acceptedTypesRecipe?.includes('FISICA') && (
            <TableRow error>
              <TableData error>
                <p>* Receitas digitais não existe coleta</p>
              </TableData>
            </TableRow>
          )}

        {needRecipe(product) &&
          acceptedTypesRecipe.length >= 1 &&
          !acceptedTypesRecipe?.includes('FISICA') && (
            <TableRow alert>
              <TableData alert>
                <div className="flex items-center gap-x-[15px]">
                  <p>
                    Atenção! Aceitamos somente{' '}
                    <span className="font-bold">receitas digitais</span> para
                    este endereço.
                  </p>
                </div>
              </TableData>
            </TableRow>
          )}
      </tbody>
    </table>
  );
}

interface ProductShippingProps {
  product: ISearchProduct;
}

export default function ProductShipping({ product }: Readonly<ProductShippingProps>) {
  const [state, setState] = useState({ step: 'Cep' });
  const [isLoading, setIsLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [shippingMethods, setShippingMethods] = useState([]);
  const [error, setError] = useState('');
  const [acceptedTypesRecipe, setAcceptedTypesRecipe] = useState([]);


  // const convertedDeliveryTiming = JSON.parse(jsonDeliveryTiming);
  const convertedDeliveryTiming = JSON.parse('[]');

  const checkRecipeTypeForShippingMethods = useCallback(
    (shippingMethods) => {
      const validtypesOfRecipe = [];

      shippingMethods.forEach(function (item) {
        const config = convertedDeliveryTiming.find(function (i) {
          if (!item.repositoryId) return i.shippingId === item.shippingMethodId;

          return i.shippingId === item.repositoryId;
        });

        if (config && 'acceptedRecipeTypes' in config) {
          config.acceptedRecipeTypes.forEach((type) => {
            if (!validtypesOfRecipe.includes(type))
              validtypesOfRecipe.push(type);
          });
        }
      });
      setAcceptedTypesRecipe(validtypesOfRecipe);
    },
    [convertedDeliveryTiming],
  );

  const handleCloseList = useCallback(() => {
    setAddress('');
    setShippingMethods([]);
    setError('');
    setState({ step: 'Cep' });
  }, []);

  const fetchShippingMethods = useMutation(
    async (cep) => {
      const response = await fetch(`/api/quotation/${cep}/`);
      if (!response.ok) {
        throw new Error('Failed to fetch shipping methods');
      }
      return response.json();
    },
    {
      onSuccess: ({ data }) => {
        // Handle successful data fetching
        setShippingMethods(data.quotationResponse || []);
        setAddress(data.address || '');

        // TODO: uncomment when new shipping api is ready

        // if (!data?.error) {
        //   checkRecipeTypeForShippingMethods(
        //     Object.values(data?.shippingMethods),
        //   );
        // } else {
        //   if (x_termolabil) {
        //     setError(
        //       'Por segurança, este medicamento não pode ser entregue no CEP indicado para preservar suas condições térmicas de transporte.',
        //     );
        //   } else if (x_psico_identifier_) {
        //     setError(
        //       'Por segurança, este medicamento não pode ser entregue no CEP indicado para cumprir com a retenção obrigatória da receita médica.',
        //     );
        //     setAcceptedTypesRecipe([]);
        //   } else {
        //     setError('Entrega indisponível para este endereço.');
        //   }
        // }
        setState({ step: 'List' });
      },
      onError: (err) => {
        setError('Erro ao buscar cep');
        setState({ step: 'Cep' });
      },
    },
  );

  const cepFormHandle: SubmitHandler<CepInput> = async ({ cep }) => {
    setIsLoading(true);
    setState({ step: 'Loading' });

    try {
      await fetchShippingMethods.mutateAsync(cep);
    } catch (error) {
      console.error('Failed to fetch shipping methods:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full mt-6">
      <label htmlFor="cep" className="text-md font-medium text-gray-800">Calcular frete e prazo</label>
      {state.step === 'Cep' && (
        <CepForm
          // email={state.email}
          onSubmit={cepFormHandle}
          isLoading={isLoading}
          // serverError={formError}
        />
      )}

      {state.step === 'Loading' && (
        <div className="flex items-center justify-center">
          <ProductLoader />
        </div>
      )}

      {state.step === 'List' && (
        <ShippingTable
          acceptedTypesRecipe={acceptedTypesRecipe}
          shippingMethods={shippingMethods}
          address={address}
          convertedDeliveryTiming={convertedDeliveryTiming}
          handleCloseList={handleCloseList}
        />
      )}
    </div>
  );
}
